import { buildLocalePath, getActiveLocaleFromPath, type LanguageCode } from "@repo/locale";

export const BOOKING_PATH = process.env.NEXT_PUBLIC_BOOKING_PATH || "booking";
export const BOOKING_BASE = `/${BOOKING_PATH}`;
export const bookingPath = (rest = ""): string => `${BOOKING_BASE}${rest}`;
export const HOME_BASE = BOOKING_BASE !== "/booking" ? BOOKING_BASE : "/";

/**
 * Segment-boundary-safe path-prefix helpers.
 *
 * Plain `startsWith` / `String.replace` treat a path as a character sequence, not
 * as `/`-delimited segments, so any base is also a prefix of longer segment names:
 *
 *   "/bookingV2/member/flight".startsWith("/booking")                     // true (!)
 *   "/bookingV2/member/flight".replace("/booking", "/booking/member")
 *     === "/booking/memberV2/member/flight"                               // spliced mid-segment
 *
 * That is not hypothetical. With NEXT_PUBLIC_BOOKING_PATH=bookingV2, a fleet where
 * one service was still built with `booking` produced exactly the string above:
 * the stale service could not strip `/bookingV2`, the unsafe replace spliced the
 * zone into the middle stranding `V2`, and the aliasing service canonicalised the
 * result back to the alias — one extra `memberV2` per round trip, unbounded, until
 * the browser gave up with ERR_TOO_MANY_REDIRECTS.
 *
 * Always compare and rewrite bases through these helpers so a version-skewed or
 * misconfigured deploy 404s loudly instead of looping.
 */

/** True when `pathname` is exactly `base`, or a segment beneath it. */
export const isUnderBase = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * Swap a leading `from` base for `to`, matching on a segment boundary only.
 * Returns `pathname` untouched when it is not under `from` — callers therefore
 * cannot silently mangle a path that belongs to a different base.
 */
export const replaceBase = (pathname: string, from: string, to: string): string =>
  isUnderBase(pathname, from) ? `${to}${pathname.slice(from.length)}` : pathname;

export const APP_BASE = process.env.NEXT_PUBLIC_APP_BASE || "/fly";

export const ZONES = ["booking", "staff", "member"] as const;

export type Zone = (typeof ZONES)[number];

export const CURRENT_ZONE = (process.env.NEXT_PUBLIC_ZONE || undefined) as Zone | undefined;

/**
 * ────────────────────────────────────────────────────────────────────────────
 * The URL contract: APP_BASE → locale → zone → rest
 * ────────────────────────────────────────────────────────────────────────────
 *
 *   /fly  /cl/es  /staff  /flight
 *   ^^^^  ^^^^^^  ^^^^^^  ^^^^^^^
 *   basePath        │       in-zone route
 *          locale — outermost after the app base (playbook Q5), so one prefix
 *          covers every zone and `hreflang`/canonical work per market.
 *
 * `APP_BASE` comes from Next's `basePath`, so it must appear in a path EXACTLY
 * ONCE and only where Next will not add it for you:
 *
 *   - in-app navigation (`<Link>`, `router.push`, `redirect()`): NEVER include it.
 *     Next prefixes basePath itself; including it yields `/fly/fly/...`.
 *     → use `zoneRoutePath` / `localeRoutePath`.
 *   - browser-level or cross-app navigation (`window.location.assign`, a redirect
 *     built in another app's middleware, an absolute URL handed to a third party):
 *     you MUST include it, because nothing else will.
 *     → use `zoneAbsoluteUrl` / `homeUrl`.
 *
 * The zone segment is a real route folder (`app/[country]/[lang]/<zone>/...`), NOT
 * part of basePath — that is what allows the locale to sit before it. Upstream puts
 * the zone in basePath instead, which forces the locale after it; if you ever see a
 * bare in-zone path like `router.push("/flight")` arriving from an upstream merge,
 * it needs the zone re-attached (the navigation layer does this).
 */

/** Locale prefix for a code, or "" when there is no active locale. */
const localeSegment = (code?: LanguageCode | null): string => (code ? buildLocalePath(code) : "");

/** Strip a leading APP_BASE from a browser path (`/fly/cl/es/x` → `/cl/es/x`). */
export const stripAppBase = (pathname: string): string =>
  isUnderBase(pathname, APP_BASE) ? pathname.slice(APP_BASE.length) || "/" : pathname;

/**
 * The active locale as seen by the browser. `window.location.pathname` still carries
 * APP_BASE, so it has to come off before the first two segments mean anything.
 */
export const activeLocaleFromBrowser = (): LanguageCode | null =>
  typeof window === "undefined"
    ? null
    : getActiveLocaleFromPath(stripAppBase(window.location.pathname));

/** In-app path (no APP_BASE — Next adds it): `/cl/es/x`. */
export const localeRoutePath = (rest = "", code?: LanguageCode | null): string =>
  `${localeSegment(code)}${rest}`;

/** In-app path inside a zone (no APP_BASE — Next adds it): `/cl/es/staff/flight`. */
export const zoneRoutePath = (
  zone: Zone | undefined,
  rest = "",
  code?: LanguageCode | null,
): string => `${localeSegment(code)}${zone ? `/${zone}` : ""}${rest}`;

/**
 * Browser-level absolute path including APP_BASE: `/fly/cl/es/staff/flight`.
 * `code` defaults to the locale in the current browser URL, so existing cross-app
 * call sites keep the market they are standing in instead of dropping to default.
 */
export const zoneAbsoluteUrl = (zone: Zone, rest = "", code?: LanguageCode | null): string =>
  `${APP_BASE}${zoneRoutePath(zone, rest, code ?? activeLocaleFromBrowser())}`;

/** Home (main app) as a browser-level absolute path: `/fly/cl/es`. */
export const homeUrl = (rest = "", code?: LanguageCode | null): string =>
  `${APP_BASE}${localeRoutePath(rest, code ?? activeLocaleFromBrowser())}`;

/** Home without a locale — the middleware will resolve and redirect to one. */
export const HOME_URL = APP_BASE;

/**
 * Cross-zone URL. Kept on upstream's name/signature so merged call sites keep
 * working, but locale-aware: the third argument (or the current browser locale)
 * lands *before* the zone, per the contract above.
 */
export const zoneUrl = (zone: Zone, rest = "", code?: LanguageCode | null): string =>
  zoneAbsoluteUrl(zone, rest, code);

/**
 * The app's own technical (non-localized) surface, namespaced by zone so the edge
 * can route it: `/fly/booking/api-site/...`. Locale never appears here — API routes
 * are locale-agnostic and take the locale as a query param when they need it
 * (see DATA‑3). Each zone app rewrites `/{zone}/{_next,api,api-site}/*` back onto
 * its own basePath-relative path in `next.config.ts`.
 */
export const CURRENT_BASE = CURRENT_ZONE ? `${APP_BASE}/${CURRENT_ZONE}` : APP_BASE;

export const apiPath = (path: string): string =>
  `${CURRENT_BASE}${path.startsWith("/") ? path : `/${path}`}`;

export const zonePath = (rest = ""): string => rest || "/";

/** True when a browser path is under `zone`, at any supported locale. */
export const isZoneUrl = (pathname: string, zone: Zone): boolean => {
  const rest = stripAppBase(pathname);
  const code = getActiveLocaleFromPath(rest);
  const withoutLocale = code ? rest.slice(buildLocalePath(code).length) || "/" : rest;
  // segment-safe on both the localized and bare forms (see replaceBase above)
  return isUnderBase(withoutLocale, `/${zone}`) || isUnderBase(rest, `/${zone}`);
};

export const zoneFromUrl = (pathname: string): Zone | null =>
  ZONES.find((zone) => isZoneUrl(pathname, zone)) ?? null;

/** A browser path reduced to its in-zone remainder: `/fly/cl/es/staff/flight` → `/flight`. */
export const toZonePath = (pathname: string): string => {
  const rest = stripAppBase(pathname);
  const code = getActiveLocaleFromPath(rest);
  const withoutLocale = code ? rest.slice(buildLocalePath(code).length) || "/" : rest;
  const zone = ZONES.find((z) => isUnderBase(withoutLocale, `/${z}`));
  if (!zone) return withoutLocale;
  return withoutLocale.slice(`/${zone}`.length) || "/";
};
